import React from "react";
import {
	BrowserRouter as Router,
	Routes,
	Route,
	Link,
	useLocation,
	Outlet,
	useNavigate,
} from "react-router-dom";
import ShowGraph from "./ShowGraph";
import Tab1 from "./Tab1";
import BottomTab from "./BottomTab";
import "../styles/LandingPage.css";
import SidebarNav from "./SidebarNav";
import Dashboard from "./Dashboard";
import VideoScriptPage from "./VideoScriptPage";
import AvatarVideo from "./AvatarVideo";
import { Paper, Typography, Box, Container, Grid, Button } from "@mui/material";
import {
	Description,
	PersonOutline,
	VideoLibrary,
	ArrowForward,
	Create,
	AutoAwesome,
} from "@mui/icons-material";

const LandingPage = () => {
	const location = useLocation();
	const navigate = useNavigate();

	return (
		<div className="bg-gray-50 min-h-screen">
			<Container maxWidth="lg" className="py-8">
				{/* Hero Section */}
				<Box className="text-center mb-12 py-8 flex flex-col items-center">
					<Typography
						variant="h3"
						component="h1"
						className="font-bold text-gray-800 mb-4"
					>
						Welcome to MagicVerse
					</Typography>
					<Box className="flex flex-row gap-2">
						<Box className="flex justify-center">
							<AutoAwesome className="text-purple-500 text-6xl" />
						</Box>
						<Typography
							variant="h6"
							className="text-gray-600 max-w-2xl mx-auto mb-6"
						>
							Create stunning videos and scripts with our
							AI-powered platform
						</Typography>
					</Box>
				</Box>

				{/* Main Cards */}
				<div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
					{/* Product Video Card */}
					<Paper
						elevation={4}
						className="rounded-xl overflow-hidden h-96 cursor-pointer transform transition-all duration-300 ease-in-out hover:shadow-xl relative group border border-gray-100"
						onClick={() => navigate("/product_video")}
					>
						{/* Gradient overlay that appears on hover */}
						<Box className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-orange-700/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

						{/* Card Header */}
						<Box className="bg-gradient-to-r from-orange-500 to-orange-600 p-4 text-white">
							<Typography
								variant="h5"
								className="font-bold text-center"
							>
								Product Video
							</Typography>
						</Box>

						{/* Card Content */}
						<Box className="flex flex-col items-center justify-center h-4/5 p-6 relative">
							<Create className="text-6xl mb-6 text-gray-300 group-hover:text-orange-500 transition-all duration-300" />

							<Typography
								variant="body1"
								className="text-center text-gray-600 group-hover:text-gray-800 transition-colors duration-300 mb-6"
							>
								Create professional product videos with AI-powered presenters
							</Typography>

							<Button
								variant="outlined"
								color="warning"
								endIcon={<ArrowForward />}
								className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
							>
								Get Started
							</Button>
						</Box>

						{/* Bottom accent bar */}
						<Box className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 to-orange-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
					</Paper>

					{/* Avatar Video Card */}
					<Paper
						elevation={4}
						className="rounded-xl overflow-hidden h-96 cursor-pointer transform transition-all duration-300 ease-in-out hover:shadow-xl relative group border border-gray-100"
						onClick={() => navigate("/avatar_video")}
					>
						{/* Gradient overlay that appears on hover */}
						<Box className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-700/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

						{/* Card Header */}
						<Box className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 text-white">
							<Typography
								variant="h5"
								className="font-bold text-center"
							>
								Avatar Video
							</Typography>
						</Box>

						{/* Card Content */}
						<Box className="flex flex-col items-center justify-center h-4/5 p-6 relative">
							<PersonOutline className="text-6xl mb-6 text-gray-300 group-hover:text-blue-500 transition-all duration-300" />

							<Typography
								variant="body1"
								className="text-center text-gray-600 group-hover:text-gray-800 transition-colors duration-300 mb-6"
							>
								Create and customize avatar videos with our
								advanced AI tools and templates
							</Typography>

							<Button
								variant="outlined"
								color="primary"
								endIcon={<ArrowForward />}
								className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
							>
								Get Started
							</Button>
						</Box>

						{/* Bottom accent bar */}
						<Box className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-blue-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
					</Paper>

					{/* Video Script Card */}
					<Paper
						elevation={4}
						className="rounded-xl overflow-hidden h-96 cursor-pointer transform transition-all duration-300 ease-in-out hover:shadow-xl relative group border border-gray-100"
						onClick={() => navigate("/video_script")}
					>
						{/* Gradient overlay that appears on hover */}
						<Box className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-green-700/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

						{/* Card Header */}
						<Box className="bg-gradient-to-r from-green-500 to-green-600 p-4 text-white">
							<Typography
								variant="h5"
								className="font-bold text-center"
							>
								Video Script
							</Typography>
						</Box>

						{/* Card Content */}
						<Box className="flex flex-col items-center justify-center h-4/5 p-6 relative">
							<Description className="text-6xl mb-6 text-gray-300 group-hover:text-green-500 transition-all duration-300" />

							<Typography
								variant="body1"
								className="text-center text-gray-600 group-hover:text-gray-800 transition-colors duration-300 mb-6"
							>
								Create and edit professional video scripts
								with ease using our AI-powered tools
							</Typography>

							<Button
								variant="outlined"
								color="success"
								endIcon={<ArrowForward />}
								className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
							>
								Get Started
							</Button>
						</Box>

						{/* Bottom accent bar */}
						<Box className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-green-400 to-green-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
					</Paper>

					{/* Tutorial Video Card */}
					<Paper
						elevation={4}
						className="rounded-xl overflow-hidden h-96 cursor-pointer transform transition-all duration-300 ease-in-out hover:shadow-xl relative group border border-gray-100"
						onClick={() => navigate("/tutorial_video")}
					>
						{/* Gradient overlay that appears on hover */}
						<Box className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-purple-700/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

						{/* Card Header */}
						<Box className="bg-gradient-to-r from-purple-500 to-purple-600 p-4 text-white">
							<Typography
								variant="h5"
								className="font-bold text-center"
							>
								Tutorial Video
							</Typography>
						</Box>

						{/* Card Content */}
						<Box className="flex flex-col items-center justify-center h-4/5 p-6 relative">
							<VideoLibrary className="text-6xl mb-6 text-gray-300 group-hover:text-purple-500 transition-all duration-300" />

							<Typography
								variant="body1"
								className="text-center text-gray-600 group-hover:text-gray-800 transition-colors duration-300 mb-6"
							>
								Learn how to create amazing videos with our comprehensive tutorials
							</Typography>

							<Button
								variant="outlined"
								color="secondary"
								endIcon={<ArrowForward />}
								className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
							>
								Get Started
							</Button>
						</Box>

						{/* Bottom accent bar */}
						<Box className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-400 to-purple-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
					</Paper>
				</div>

				{/* Feature Section */}
				<Box className="bg-white rounded-xl shadow-md p-8 mb-12">
					<Typography
						variant="h5"
						className="font-bold text-gray-800 mb-6 text-center"
					>
						Why Choose MagicVerse?
					</Typography>

					<div className="flex flex-col md:flex-row justify-center items-center gap-8 w-full mx-auto">
						<Grid item xs={12} md={4} className="text-center">
							<Box className="p-4">
								<Create className="text-4xl text-purple-500 mb-4" />
								<Typography
									variant="h6"
									className="font-medium mb-2"
								>
									Easy Script Creation
								</Typography>
								<Typography
									variant="body2"
									className="text-gray-600"
								>
									Generate professional scripts with our
									AI-powered tools in minutes
								</Typography>
							</Box>
						</Grid>

						<Grid item xs={12} md={4} className="text-center">
							<Box className="p-4">
								<PersonOutline className="text-4xl text-indigo-500 mb-4" />
								<Typography
									variant="h6"
									className="font-medium mb-2"
								>
									Realistic Avatars
								</Typography>
								<Typography
									variant="body2"
									className="text-gray-600"
								>
									Choose from a variety of lifelike avatars
									for your videos
								</Typography>
							</Box>
						</Grid>

						<Grid item xs={12} md={4} className="text-center">
							<Box className="p-4">
								<VideoLibrary className="text-4xl text-green-500 mb-4" />
								<Typography
									variant="h6"
									className="font-medium mb-2"
								>
									Full Video Creation
								</Typography>
								<Typography
									variant="body2"
									className="text-gray-600"
								>
									Generate complete video presentations with
									just a few clicks
								</Typography>
							</Box>
						</Grid>
					</div>
				</Box>
			</Container>
		</div>
	);
};

export default LandingPage;
